import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

/**
 * Thin progress meter pinned to the top edge of the Page 2 hero card.
 *
 * Each of the three micro-steps (drop artwork → fine-tune size/qty →
 * quote ready) advances independently as the underlying state updates
 * — the green bar animates across the card for immediate visual
 * feedback, and each step label gets a crisp amber check when done.
 *
 * Intentionally chromeless: no background panel, no border — just the
 * bar + labels. The hero card's ambient gradient reads through.
 */
const STEPS = [
  { id: 1, label: 'Drop artwork' },
  { id: 2, label: 'Fine-tune size' },
  { id: 3, label: 'Quote ready' },
];

export const Page2ProgressMeter = ({ step1Done = false, step2Done = false, step3Done = false }) => {
  const flags = [step1Done, step2Done, step3Done];
  const completed = flags.filter(Boolean).length;
  // Pct is based on the HIGHEST contiguous completed step so we don't
  // fill past step 1 when step 2 is still blank.
  let contiguous = 0;
  for (const f of flags) {
    if (f) contiguous += 1;
    else break;
  }
  const pct = (contiguous / STEPS.length) * 100;

  return (
    <div className="relative mb-4" data-testid="page2-progress-meter">
      {/* Rail — iter114s.69: dark-on-dark white/10 rail flipped to a
          soft EG3 green tint so it reads on the new pale-green panel. */}
      <div className="relative h-1.5 rounded-full bg-[#6FBF73]/15 overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#6FBF73] via-[#A8D8AC] to-[#6FBF73] rounded-full shadow-[0_0_12px_rgba(111,191,115,0.45)]"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          data-testid="page2-progress-fill"
        />
      </div>
      {/* Labels */}
      <div className="mt-2 flex items-center justify-between text-[11px] sm:text-xs">
        {STEPS.map(({ id, label }, idx) => {
          const done = flags[idx];
          return (
            <div
              key={id}
              className={`flex items-center gap-1.5 transition-colors ${
                done ? 'text-[#2F5D3A]' : 'text-[#1F3D2C]/55'
              }`}
              data-testid={`page2-progress-step-${id}`}
            >
              <span
                className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-black transition-all ${
                  done
                    ? 'bg-[#6FBF73] text-white shadow-[0_0_8px_rgba(111,191,115,0.6)] scale-110'
                    : 'bg-white/70 text-[#1F3D2C]/70 ring-1 ring-[#6FBF73]/25'
                }`}
              >
                {done ? <Check className="w-2.5 h-2.5" strokeWidth={3.5} /> : id}
              </span>
              <span
                className={`whitespace-nowrap font-semibold tracking-wide uppercase text-[10px] sm:text-[11px] ${
                  done ? '' : 'opacity-80'
                }`}
              >
                Step {id} — {label}
              </span>
            </div>
          );
        })}
      </div>
      {completed === STEPS.length && (
        <motion.div
          aria-hidden="true"
          className="absolute -right-1 -top-1 w-3 h-3 rounded-full bg-[#6FBF73]/70"
          animate={{ scale: [1, 1.8, 1], opacity: [0.8, 0, 0.8] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeOut' }}
        />
      )}
    </div>
  );
};

export default Page2ProgressMeter;
